/**
 * Ordenación EN CLIENTE de las filas de los modales de vincular (2026-10-26).
 *
 * Los dos modales («Vincular OT» y «Vincular con tramo») paginan en cliente
 * sobre una lista que ya tienen entera, y el de OT además MEZCLA genéricas y
 * campañas, que llegan de dos consultas. Las cabeceras ordenables de la tabla
 * escriben `sortField`/`orderBy` en el cursor, pero nadie ordenaba con ellos:
 * pulsar una cabecera cambiaba la flecha y no las filas.
 *
 * - Los vacíos van SIEMPRE al final, en las dos direcciones.
 * - Un PK es la tupla (km, metro) y se compara en ese orden, nunca como
 *   `km * 1000 + metro`: hay kilómetros de más de 1.000 m
 *   (`.claude/rules/pk-kilometro-largo.md`).
 * - Con empate se respeta el orden de entrada (orden estable).
 */
export type SortDirection = 'asc' | 'desc';

/** Número (fechas ya convertidas), texto o PK como tupla (km, metro). */
export type SortValue =
  | number
  | string
  | readonly [number, number]
  | null
  | undefined;

const isEmpty = (value: SortValue): boolean =>
  value == null ||
  value === '' ||
  (typeof value === 'number' && Number.isNaN(value));

function compareValues(a: SortValue, b: SortValue): number {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a[0] - b[0] || a[1] - b[1];
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'es', { numeric: true });
}

export function sortRows<T>(
  rows: readonly T[],
  valueOf: (row: T) => SortValue,
  direction: SortDirection,
): T[] {
  const factor = direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: valueOf(row) }))
    .sort((a, b) => {
      const aEmpty = isEmpty(a.value);
      const bEmpty = isEmpty(b.value);
      if (aEmpty || bEmpty) {
        if (aEmpty === bEmpty) return a.index - b.index;
        return aEmpty ? 1 : -1;
      }
      return compareValues(a.value, b.value) * factor || a.index - b.index;
    })
    .map(({ row }) => row);
}

/** PK como tupla ordenable; sin km no hay PK (vacío). Metro ausente = 0. */
export const pkSortValue = (
  km: number | null | undefined,
  meter: number | null | undefined,
): SortValue => (km == null ? null : [km, meter ?? 0]);

/** Fecha ISO como número ordenable; sin fecha = vacío. */
export const dateSortValue = (iso: string | null | undefined): SortValue =>
  iso ? Date.parse(iso) : null;
